import dayjs from 'dayjs';
import { LegacyCalendarStateSerializer } from '../../../serialization/legacy-calendar-state-serializer.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { DayOfYear } from '../day-of-year.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { CalendarDayState } from './calendar-day-state.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { CalendarSnapshot } from './calendar-snapshot.js';
import { IDailyCalendarSnapshot } from '../../../contracts/daily-calendar-snapshot.js';
import {
  cloneMap,
  DataModelBuilder,
  isSameData,
  isSameMap,
  SerializationContext,
  SerializedData,
} from '../../dto/dto.js';

export class LegacyCalendarSnapshot extends CalendarSnapshot implements IDailyCalendarSnapshot {
  static readonly TYPE_ID: string = '3aabd3d773e34aab89ca32cc7f9fd6a6';

  public readonly date: dayjs.Dayjs;

  constructor(
    public readonly dayOfYear: DayOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly calendars: Map<string, CalendarDayState>,
  ) {
    super();
    this.date = this.dayOfYear.toDate(TimeZone.ofRegion(this.timezone));
  }

  get $typeId(): string {
    return LegacyCalendarSnapshot.TYPE_ID;
  }

  get timestamp(): number {
    return this.date.valueOf();
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyCalendarSnapshot {
    return new LegacyCalendarSnapshot(
        context.fromData<DayOfYear>(MapUtils.get<SerializedData>(map, 'dayOfYear')),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        LegacyCalendarStateSerializer.deserialize(context.fromBinaryBlob(MapUtils.getRaw(map, 'data')))
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'dayOfYear': context.toData(this.dayOfYear),
      'date': context.encodeDate(this.date),
      'timezone': fromEnumAsString(this.timezone),
      'data': context.toBinaryBlob(LegacyCalendarStateSerializer.serialize(this.calendars)),
    };
  }

  clone(): this {
    return new LegacyCalendarSnapshot(
        this.dayOfYear.clone(),
        this.timezone,
        cloneMap(this.calendars, (cs) => cs.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.calendars, this.calendars, (a, b) => isSameData(a, b)) &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        other.timezone === this.timezone;
  }

  getCalendarState(uniqueId?: string): CalendarDayState | null {
    return this.calendars.get(uniqueId ?? CalendarSnapshot.GLOBAL_STATE_ID) ?? null;
  }

  getOrCreateCalendarState(uniqueId: string): CalendarDayState {
    let calendarState = this.calendars.get(uniqueId ?? CalendarSnapshot.GLOBAL_STATE_ID) ?? null;
    if(isNullOrUndefined(calendarState)) {
      calendarState = CalendarDayState.empty(uniqueId);
      this.calendars.set(uniqueId, calendarState);
    }

    return calendarState;
  }

  toDiagnosticsObject(): SerializedData {
    const calendars: SerializedData = {};
    this.calendars.forEach((v, k) => {
      calendars[k] = v.toDiagnosticsObject();
    });

    return {
      'dayOfYear': this.dayOfYear.uniqueId,
      'date': TimeZone.encodeDate(this.date),
      'timezone': fromEnumAsString(this.timezone),
      'calendars': calendars,
    };
  }
}

export class LegacyCalendarSnapshotBuilder extends DataModelBuilder<LegacyCalendarSnapshot> {
  build(context: SerializationContext, map: SerializedData): LegacyCalendarSnapshot {
    return LegacyCalendarSnapshot.fromMap(context, map);
  }
}
